import * as React from 'react';
import { ArrowDownRight, ArrowUpRight, type LucideIcon } from 'lucide-react';
import { Card } from '../ui/card';
import { cn } from '../../lib/utils';
import type { Tone } from '../../theme/colors';

export interface MetricDelta {
  /** Delta value rendered as text (e.g. "+12.4%", "−820"). */
  label: string;
  /** Direction. `flat` hides the arrow. */
  direction?: 'up' | 'down' | 'flat';
  /** Optional context label after the delta (e.g. "vs last week"). */
  context?: string;
}

export interface MetricCardProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Short label above the value (e.g. "Open orders"). */
  label: React.ReactNode;
  /** Primary metric value. */
  value: React.ReactNode;
  /** Unit suffix (e.g. "₹", "%", "ms"). Rendered smaller. */
  unit?: React.ReactNode;
  /** One-line hint below the value (e.g. "vs same window yesterday"). */
  hint?: React.ReactNode;
  /** Tone — controls the value color subtly. Defaults to neutral. */
  tone?: Tone;
  /** Lucide icon. Rendered as a neutral chip in the corner. */
  icon?: LucideIcon;
  /** Up/down delta indicator. */
  delta?: MetricDelta;
  /** When true, render skeleton placeholders instead of values. */
  loading?: boolean;
}

const toneToValueClass: Record<Tone, string> = {
  neutral: 'text-foreground',
  primary: 'text-primary',
  accent: 'text-accent',
  success: 'text-success',
  warning: 'text-warning',
  destructive: 'text-destructive',
  info: 'text-info',
};

/**
 * MetricCard — single-metric KPI tile.
 *
 * Embodies the "Operational Quiet" pattern: the icon stays neutral, the
 * label is muted, and only the *value* (or its delta) carries tonal weight.
 */
export const MetricCard = React.forwardRef<HTMLDivElement, MetricCardProps>(
  (
    {
      label,
      value,
      unit,
      hint,
      tone = 'neutral',
      icon: Icon,
      delta,
      loading,
      className,
      ...props
    },
    ref,
  ) => {
    const deltaDirection = delta?.direction ?? 'flat';
    const DeltaIcon =
      deltaDirection === 'up'
        ? ArrowUpRight
        : deltaDirection === 'down'
          ? ArrowDownRight
          : null;
    const deltaClass =
      deltaDirection === 'up'
        ? 'text-success'
        : deltaDirection === 'down'
          ? 'text-destructive'
          : 'text-muted-foreground';

    return (
      <Card
        ref={ref}
        className={cn('flex flex-col gap-3 p-5', className)}
        {...props}
      >
        <div className="flex items-start justify-between gap-3">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {label}
          </span>
          {Icon && (
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-muted text-muted-foreground">
              <Icon className="h-4 w-4" />
            </span>
          )}
        </div>

        {loading ? (
          <div className="h-8 w-24 animate-pulse rounded-md bg-muted/70" />
        ) : (
          <div className="flex items-baseline gap-1.5">
            <span
              className={cn(
                'tnum text-3xl font-semibold tracking-tight',
                toneToValueClass[tone],
              )}
            >
              {value}
            </span>
            {unit && (
              <span className="text-sm font-medium text-muted-foreground">
                {unit}
              </span>
            )}
          </div>
        )}

        {(delta || hint) && (
          <div className="flex items-center justify-between gap-2 text-xs">
            {delta ? (
              <span className={cn('inline-flex items-center gap-1 tnum font-medium', deltaClass)}>
                {DeltaIcon && <DeltaIcon className="h-3.5 w-3.5" />}
                {delta.label}
                {delta.context && (
                  <span className="font-normal text-muted-foreground">
                    {delta.context}
                  </span>
                )}
              </span>
            ) : (
              <span />
            )}
            {hint && (
              <span className="text-muted-foreground">{hint}</span>
            )}
          </div>
        )}
      </Card>
    );
  },
);
MetricCard.displayName = 'MetricCard';
